import React, { useMemo } from 'react'
import { Tooltip, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import type { DockAction } from '../BaseNodeTypes'
import { NodeInteractionZone } from '../NodeInteractionZone'
import {
  ActionsContainer,
  ActionGroup,
  GroupLabel,
  ButtonRow,
  ActionButton,
  DropdownItemWrapper,
  DropdownItemTitle,
  DropdownItemSubtitle,
} from '../styles/CardActionsStyles'

/** Accent color by action group type. */
const GROUP_COLORS: Record<string, string> = {
  branch: 'var(--color-primary)',
}

const DEFAULT_COLOR = 'var(--color-primary)'

interface CardActionsProps {
  actions: DockAction[]
}

/**
 * Renders dock action groups as inline CTA buttons inside the card body.
 *
 * Three rendering modes:
 * - `dropdownChildren`: single button whose label matches the group label, opens a
 *   dropdown menu with title + subtitle per item.
 * - `children`: labelled column with side-by-side buttons (legacy inline group).
 * - no children: standalone button.
 */
export const CardActions = React.memo(
  ({ actions }: CardActionsProps): React.ReactElement | null => {
    const { dropdownGroups, inlineGroups, singles } = useMemo(() => {
      const dg: DockAction[] = []
      const ig: DockAction[] = []
      const s: DockAction[] = []
      for (const action of actions) {
        if (action.dropdownChildren && action.dropdownChildren.length > 0) {
          dg.push(action)
        } else if (action.children && action.children.length > 0) {
          ig.push(action)
        } else {
          s.push(action)
        }
      }
      return { dropdownGroups: dg, inlineGroups: ig, singles: s }
    }, [actions])

    if (dropdownGroups.length === 0 && inlineGroups.length === 0 && singles.length === 0) {
      return null
    }

    return (
      <NodeInteractionZone>
        <ActionsContainer>
          {/* Dropdown-trigger groups: single button → dropdown menu */}
          {dropdownGroups.map((group) => {
            const color = GROUP_COLORS[group.group] ?? DEFAULT_COLOR
            const menuItems: MenuProps['items'] = group.dropdownChildren?.map((child) => {
              const ChildIcon = child.icon
              return {
                key: child.id,
                icon: <ChildIcon size={14} />,
                label: (
                  <DropdownItemWrapper>
                    <DropdownItemTitle>{child.label}</DropdownItemTitle>
                    {child.subtitle && (
                      <DropdownItemSubtitle>{child.subtitle}</DropdownItemSubtitle>
                    )}
                  </DropdownItemWrapper>
                ),
                disabled: child.disabled,
                onClick: ({ domEvent }: { domEvent: React.MouseEvent | React.KeyboardEvent }) => {
                  domEvent.stopPropagation()
                  child.onClick(domEvent as React.MouseEvent)
                },
              }
            })

            return (
              <ActionGroup key={group.id}>
                <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomLeft">
                  <ActionButton
                    $accentColor={color}
                    $variant="filled"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {group.label}
                  </ActionButton>
                </Dropdown>
              </ActionGroup>
            )
          })}

          {/* Inline groups: labelled column with side-by-side buttons */}
          {inlineGroups.map((group) => {
            const color = GROUP_COLORS[group.group] ?? DEFAULT_COLOR
            return (
              <ActionGroup key={group.id}>
                <GroupLabel>{group.label}</GroupLabel>
                <ButtonRow>
                  {group.children?.map((child) => {
                    const button = (
                      <ActionButton
                        key={child.id}
                        $accentColor={color}
                        $variant="filled"
                        disabled={child.disabled}
                        onClick={(e) => {
                          e.stopPropagation()
                          child.onClick(e)
                        }}
                      >
                        {child.label}
                      </ActionButton>
                    )

                    if (child.disabled && child.disabledReason) {
                      return (
                        <Tooltip
                          key={child.id}
                          title={child.disabledReason}
                          placement="bottom"
                          mouseEnterDelay={0.3}
                        >
                          {button}
                        </Tooltip>
                      )
                    }

                    return button
                  })}
                </ButtonRow>
              </ActionGroup>
            )
          })}

          {/* Standalone actions */}
          {singles.length > 0 && (
            <ActionGroup>
              <ButtonRow>
                {singles.map((action) => {
                  const color = GROUP_COLORS[action.group] ?? DEFAULT_COLOR
                  return (
                    <ActionButton
                      key={action.id}
                      $accentColor={color}
                      $variant="filled"
                      disabled={action.disabled}
                      onClick={(e) => {
                        e.stopPropagation()
                        action.onClick(e)
                      }}
                    >
                      {action.label}
                    </ActionButton>
                  )
                })}
              </ButtonRow>
            </ActionGroup>
          )}
        </ActionsContainer>
      </NodeInteractionZone>
    )
  }
)

CardActions.displayName = 'CardActions'
